import { Root, Label, Field, Control, Submit } from '@radix-ui/react-form';
import { useEffect, useRef } from 'react';
import { Button, Icon } from '@zenid/web';
import { flex } from '../../../../../styled-system/patterns';
import { css } from '../../../../../styled-system/css';
import { input } from '../../../../../styled-system/recipes';
import type { UseChangeFormProps } from '../../hooks/useChangeForm';

export type ChangeFormProps = Pick<UseChangeFormProps, 'onSubmit' | 'register' | 'formState'>;

export function ChangeForm({ onSubmit, register, formState }: ChangeFormProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const labelRef = useRef<HTMLLabelElement | null>(null);
  const { ref, ...rest } = register('email');

  const {
    root: rootStyle,
    label: labelStyle,
    focusedLabel: focusedLabelStyle,
    filledLabel: filledLabelStyle,
    input: inputStyle,
    errorLabel: errorLabelStyle,
    focusedErrorLabel: focusedErrorLabelStyle,
    filledErrorLabel: filledErrorLabelStyle,
    errorInput: errorInputStyle,
  } = input();

  useEffect(() => {
    const input = inputRef.current;
    const label = labelRef.current;
    if (!input || !label) {
      return;
    }

    input.className = formState.errors.email ? errorInputStyle : inputStyle;
    // エラー状態中に正しい値を入力してもfocusout時に、formState.errors.emailが残っているので以下で拾いスタイルを戻してあげる
    if (input.value === '') {
      label.className = formState.errors.email ? errorLabelStyle : labelStyle;
    } else {
      label.className = formState.errors.email ? filledErrorLabelStyle : filledLabelStyle;
    }

    const applyClassNameOnFocus = () => {
      label.className = formState.errors.email ? focusedErrorLabelStyle : focusedLabelStyle;
    };
    const applyClassNameOnFocusOut = () => {
      if (input.value === '') {
        label.className = formState.errors.email ? errorLabelStyle : labelStyle;
      } else {
        label.className = formState.errors.email ? filledErrorLabelStyle : filledLabelStyle;
      }
    };
    input.addEventListener('focus', applyClassNameOnFocus);
    input.addEventListener('focusout', applyClassNameOnFocusOut);
    return () => {
      input.removeEventListener('focus', applyClassNameOnFocus);
      input.removeEventListener('focusout', applyClassNameOnFocusOut);
    };
  }, [
    formState.errors.email,
    filledLabelStyle,
    focusedLabelStyle,
    labelStyle,
    inputStyle,
    focusedErrorLabelStyle,
    errorLabelStyle,
    filledErrorLabelStyle,
    errorInputStyle,
  ]);

  return (
    <Root
      className={flex({
        flexDirection: 'column',
        justifyContent: 'center',
        paddingLeft: '{spacing.20}',
        paddingRight: '{spacing.20}',
        paddingBottom: '{spacing.40}',
        sm: {
          paddingLeft: '{spacing.40}',
          paddingRight: '{spacing.40}',
          paddingBottom: '{spacing.40}',
        },
      })}
      onSubmit={onSubmit}
    >
      <Field id="email-field" name="email" className={rootStyle}>
        <Label ref={labelRef} className={labelStyle} aria-labelledby="email-field">
          メールアドレス
        </Label>
        <Control asChild>
          <input
            id="email"
            type="email"
            required
            className={inputStyle}
            ref={(e) => {
              ref(e);
              inputRef.current = e;
            }}
            {...rest}
          />
        </Control>
      </Field>
      {formState.errors.email && (
        <div className={flex({ gap: '{spacing.4}', alignItems: 'center', marginTop: '{spacing.8}' })}>
          <Icon name="error_fill_on" size="s" color="colors.action.warning.main" />
          <p
            className={css({ textStyle: 'caption.m', color: '{colors.action.warning.main}' })}
          >{`${formState.errors.email.message}`}</p>
        </div>
      )}
      <Submit asChild>
        <Button
          type="submit"
          className={css({ marginTop: '{spacing.24}' })}
          color="primary"
          size="l"
          stretch="container"
        >
          設定する
        </Button>
      </Submit>
    </Root>
  );
}
